import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import LanguageSwitcher from "@/components/feature/LanguageSwitcher";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const { isLoggedIn, user, logout } = useAuth();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleLogout = () => {
    logout();
    setProfileOpen(false);
    navigate("/");
  };

  const navLinks = [
    { label: t("nav.home"), href: "/" },
    { label: t("nav.creators"), href: "/artists" },
    { label: t("nav.campaigns"), href: "/quests" },
    { label: t("nav.rewards"), href: "/rewards" },
    { label: t("nav.brands"), href: "/brands" },
    { label: t("nav.community"), href: "/community" },
    { label: t("nav.pricing"), href: "/pricing" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#0a0a0f]/95 backdrop-blur-md border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 cursor-pointer">
          <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#ff6eb4] to-[#4fc3f7]">
            <i className="ri-sparkling-2-fill text-white text-base" />
          </div>
          <span className="text-white font-black text-lg tracking-tight">CreatorVerse</span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.href}
              className="text-white/70 hover:text-white text-sm font-medium transition-colors duration-200 relative group cursor-pointer whitespace-nowrap"
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-[#ff6eb4] to-[#4fc3f7] group-hover:w-full transition-all duration-300" />
            </Link>
          ))}
        </div>

        {/* Right Actions */}
        <div className="hidden md:flex items-center gap-3">
          <LanguageSwitcher />
          <Link
            to="/artist-register"
            className="px-4 py-2 rounded-full border border-white/20 text-white/70 text-sm font-medium whitespace-nowrap cursor-pointer hover:border-white/40 hover:text-white transition-all"
          >
            {t("nav.registerCreator")}
          </Link>
          {isLoggedIn && user && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#ff6eb4]/10 to-[#4fc3f7]/10 border border-white/15">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-copper-coin-line text-[#ffd700] text-sm" />
              </div>
              <span className="text-[#ffd700] text-sm font-bold whitespace-nowrap">{user.tokens.toLocaleString()}</span>
              <span className="text-white/40 text-xs whitespace-nowrap">{t("nav.tokens")}</span>
            </div>
          )}
          {isLoggedIn && user ? (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setProfileOpen((p) => !p)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/20 hover:border-white/40 transition-all cursor-pointer"
              >
                <div className="w-6 h-6 rounded-full overflow-hidden">
                  <img src={user.avatar} alt={user.nickname} className="w-full h-full object-cover object-top" />
                </div>
                <span className="text-white text-sm font-medium whitespace-nowrap">{user.nickname}</span>
                <i className={`ri-arrow-down-s-line text-white/50 text-sm transition-transform ${profileOpen ? "rotate-180" : ""}`} />
              </button>
              {profileOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-[#0f0f1a] border border-white/15 rounded-xl overflow-hidden z-50">
                  <div className="px-4 py-3 border-b border-white/8">
                    <p className="text-white text-sm font-bold">{user.nickname}</p>
                    <p className="text-white/40 text-xs truncate">{user.email}</p>
                  </div>
                  <Link
                    to="/mypage"
                    onClick={() => setProfileOpen(false)}
                    className="flex items-center gap-2.5 px-4 py-3 text-white/70 text-sm hover:bg-white/5 hover:text-white transition-all cursor-pointer"
                  >
                    <i className="ri-user-line text-sm" />
                    {t("nav.mypage")}
                  </Link>
                  <Link
                    to="/mypage"
                    onClick={() => setProfileOpen(false)}
                    className="flex items-center gap-2.5 px-4 py-3 text-white/70 text-sm hover:bg-white/5 hover:text-white transition-all cursor-pointer"
                  >
                    <i className="ri-settings-3-line text-sm" />
                    {t("nav.settings")}
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2.5 px-4 py-3 text-white/50 text-sm hover:bg-white/5 hover:text-white/80 transition-all cursor-pointer whitespace-nowrap border-t border-white/8"
                  >
                    <i className="ri-logout-box-line text-sm" />
                    {t("nav.logout")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Link
                to="/login"
                className="px-4 py-2 rounded-full border border-white/20 text-white/70 text-sm font-medium whitespace-nowrap cursor-pointer hover:border-white/40 hover:text-white transition-all"
              >
                {t("nav.login")}
              </Link>
              <Link
                to="/signup"
                className="px-4 py-2 rounded-full bg-gradient-to-r from-[#ff6eb4] to-[#4fc3f7] text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity"
              >
                {t("nav.start")}
              </Link>
            </div>
          )}
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden w-9 h-9 flex items-center justify-center text-white cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`} />
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-[#0a0a0f]/98 backdrop-blur-md border-t border-white/10 px-6 py-4 flex flex-col gap-4">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.href}
              className="text-white/80 hover:text-white text-base font-medium cursor-pointer"
              onClick={() => setMobileOpen(false)}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/artist-register"
            className="mt-1 px-4 py-2 rounded-full border border-white/20 text-white/70 text-sm font-medium text-center cursor-pointer whitespace-nowrap"
            onClick={() => setMobileOpen(false)}
          >
            {t("nav.registerCreator")}
          </Link>
          <Link
            to="/signup"
            className="px-4 py-2 rounded-full bg-gradient-to-r from-[#ff6eb4] to-[#4fc3f7] text-white text-sm font-semibold text-center cursor-pointer whitespace-nowrap"
            onClick={() => setMobileOpen(false)}
          >
            {t("nav.start")}
          </Link>
          <div className="pt-1 border-t border-white/10">
            <LanguageSwitcher /></div>
        </div>
      )}
    </nav>
  );
}
